import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Loader2, ShieldCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { api, ErroApi } from '@/lib/api';
import { CabecalhoPagina } from '@/components/cabecalho-pagina';
import { Botao } from '@/components/ui/button';
import { Entrada } from '@/components/ui/input';
import { Campo } from '@/components/ui/campo';
import { Esqueleto } from '@/components/ui/skeleton';
import {
  Cartao,
  CartaoCabecalho,
  CartaoConteudo,
  CartaoDescricao,
  CartaoTitulo,
} from '@/components/ui/card';
import {
  Dialogo,
  DialogoCabecalho,
  DialogoConteudo,
  DialogoCorpo,
  DialogoDescricao,
  DialogoRodape,
  DialogoTitulo,
} from '@/components/ui/dialog';

const CONFIRMACAO = 'LIMPAR TUDO';

interface ResumoLimpeza {
  solicitacoes: number;
  anexos: number;
  notificacoes: number;
  historico: number;
  auditoria: number;
  logsIntegracao: number;
  ubs: number;
  especialidades: number;
  procedimentos: number;
  locais: number;
  profissionais: number;
  feriados: number;
  usuariosRemovidos: number;
  administradores: number;
  ultimoProtocolo: string | null;
}

/** Rótulo e chave de cada grupo que desaparece. */
const GRUPOS: { chave: keyof ResumoLimpeza; rotulo: string }[] = [
  { chave: 'solicitacoes', rotulo: 'Solicitações' },
  { chave: 'anexos', rotulo: 'Documentos anexados' },
  { chave: 'notificacoes', rotulo: 'Notificações' },
  { chave: 'historico', rotulo: 'Eventos de histórico' },
  { chave: 'auditoria', rotulo: 'Registros de auditoria' },
  { chave: 'logsIntegracao', rotulo: 'Logs de integração' },
  { chave: 'ubs', rotulo: 'UBS' },
  { chave: 'especialidades', rotulo: 'Especialidades' },
  { chave: 'procedimentos', rotulo: 'Procedimentos' },
  { chave: 'locais', rotulo: 'Locais de atendimento' },
  { chave: 'profissionais', rotulo: 'Profissionais' },
  { chave: 'feriados', rotulo: 'Feriados' },
  { chave: 'usuariosRemovidos', rotulo: 'Usuários (UBS e Regulação)' },
];

const PRESERVADO = [
  'Conexão com o PEC, eSUS Regulação, servidor de e-mail e n8n',
  'Modelos de e-mail',
  'Configurações do sistema (protocolo, prazo, retenção)',
  'Contas de administrador',
];

export function PaginaZonaDePerigo() {
  const queryClient = useQueryClient();
  const [dialogoAberto, setDialogoAberto] = React.useState(false);
  const [confirmacao, setConfirmacao] = React.useState('');
  const [senha, setSenha] = React.useState('');

  const { data: resumo, isPending } = useQuery({
    queryKey: ['limpeza-resumo'],
    queryFn: () => api.get<ResumoLimpeza>('/manutencao/limpeza/resumo'),
  });

  const limpar = useMutation({
    mutationFn: () =>
      api.post<{ resumo: ResumoLimpeza; arquivosRemovidos: number }>('/manutencao/limpeza', {
        confirmacao,
        senha,
      }),
    onSuccess: async (r) => {
      fechar();
      toast.success(
        `Base limpa: ${r.resumo.solicitacoes} solicitação(ões) e ${r.arquivosRemovidos} documento(s) removidos.`,
      );
      // Praticamente todo cadastro sumiu; recarregar por partes deixaria a
      // navegação mostrando dado que não existe mais.
      queryClient.clear();
      await queryClient.invalidateQueries();
    },
    onError: (erro) =>
      toast.error(erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível limpar.'),
  });

  const fechar = () => {
    setDialogoAberto(false);
    setConfirmacao('');
    setSenha('');
  };

  if (isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Esqueleto className="h-8 w-full max-w-80" />
        <Esqueleto className="h-96 w-full max-w-2xl" />
      </div>
    );
  }

  const totalApagado = GRUPOS.reduce((soma, g) => soma + Number(resumo?.[g.chave] ?? 0), 0);

  return (
    <div className="max-w-2xl">
      <CabecalhoPagina
        titulo="Zona de perigo"
        descricao="Limpeza dos dados após um treinamento, preservando a configuração do sistema."
      />

      <div className="border-status-devolvido-borda bg-status-devolvido-fundo text-status-devolvido-texto mb-5 flex items-start gap-2 rounded-md border p-3 text-sm">
        <AlertTriangle className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        <div>
          <p className="font-medium">Isto não distingue dado de treinamento de dado real.</p>
          <p className="mt-0.5">
            Se o sistema já está em operação, o caminho é a <strong>Retenção de dados</strong>, que
            anonimiza sem apagar o histórico. Esta tela existe para zerar a aplicação antes de
            entrar em produção.
          </p>
        </div>
      </div>

      <Cartao className="mb-5">
        <CartaoCabecalho>
          <CartaoTitulo className="flex items-center gap-2">
            <Trash2 className="size-4" aria-hidden="true" />O que será apagado
          </CartaoTitulo>
          <CartaoDescricao>
            {totalApagado === 0
              ? 'Não há nada a apagar — a base já está limpa.'
              : `${totalApagado} registro(s) no total.`}
          </CartaoDescricao>
        </CartaoCabecalho>

        <CartaoConteudo>
          <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
            {GRUPOS.map((grupo) => {
              const quantidade = Number(resumo?.[grupo.chave] ?? 0);
              return (
                <div
                  key={grupo.chave}
                  className="border-borda flex items-baseline justify-between gap-3 border-b pb-1.5 last:border-0"
                >
                  <dt className="text-silenciado-texto text-sm">{grupo.rotulo}</dt>
                  <dd
                    className={`text-sm font-semibold tabular-nums ${
                      quantidade > 0 ? 'text-status-devolvido-texto' : 'text-silenciado-texto'
                    }`}
                  >
                    {quantidade}
                  </dd>
                </div>
              );
            })}
          </dl>

          {resumo?.ultimoProtocolo && (
            <p className="text-silenciado-texto mt-4 text-sm">
              Último protocolo emitido: <strong>{resumo.ultimoProtocolo}</strong>. Depois da
              limpeza, a numeração recomeça do primeiro.
            </p>
          )}
        </CartaoConteudo>
      </Cartao>

      <Cartao>
        <CartaoCabecalho>
          <CartaoTitulo className="flex items-center gap-2">
            <ShieldCheck className="size-4" aria-hidden="true" />O que permanece
          </CartaoTitulo>
        </CartaoCabecalho>
        <CartaoConteudo>
          <ul className="flex flex-col gap-1.5 text-sm">
            {PRESERVADO.map((item) => (
              <li key={item} className="flex items-start gap-2">
                <span className="text-status-agendado-texto mt-0.5" aria-hidden="true">
                  ✓
                </span>
                {item}
              </li>
            ))}
          </ul>
          <p className="text-silenciado-texto mt-3 text-sm">
            {resumo?.administradores === 1
              ? 'Uma conta de administrador permanece.'
              : `${resumo?.administradores} contas de administrador permanecem.`}
          </p>
        </CartaoConteudo>
      </Cartao>

      <div className="mt-5">
        <Botao
          type="button"
          variante="destrutivo"
          disabled={totalApagado === 0}
          onClick={() => setDialogoAberto(true)}
        >
          <Trash2 aria-hidden="true" />
          Limpar dados do treinamento
        </Botao>
        {totalApagado === 0 && <p className="text-silenciado-texto mt-2 text-xs">Nada a apagar.</p>}
      </div>

      <Dialogo open={dialogoAberto} onOpenChange={(aberto) => (aberto ? null : fechar())}>
        <DialogoConteudo>
          <DialogoCabecalho>
            <DialogoTitulo>Apagar {totalApagado} registro(s)</DialogoTitulo>
            <DialogoDescricao>
              Não há como desfazer. Um backup anterior à limpeza é a única forma de recuperar —
              confira se existe um antes de prosseguir.
            </DialogoDescricao>
          </DialogoCabecalho>

          <DialogoCorpo>
            <Campo
              id="limpeza-confirmacao"
              rotulo={`Digite ${CONFIRMACAO} para confirmar`}
              obrigatorio
            >
              <Entrada
                autoFocus
                autoComplete="off"
                value={confirmacao}
                onChange={(evento) => setConfirmacao(evento.target.value)}
              />
            </Campo>

            <Campo
              id="limpeza-senha"
              rotulo="Sua senha"
              obrigatorio
              ajuda="Pedimos de novo para que uma sessão esquecida aberta não zere o banco."
            >
              <Entrada
                type="password"
                autoComplete="current-password"
                value={senha}
                onChange={(evento) => setSenha(evento.target.value)}
              />
            </Campo>
          </DialogoCorpo>

          <DialogoRodape>
            <Botao type="button" variante="contorno" onClick={fechar}>
              Cancelar
            </Botao>
            <Botao
              type="button"
              variante="destrutivo"
              disabled={confirmacao !== CONFIRMACAO || senha.length === 0 || limpar.isPending}
              onClick={() => limpar.mutate()}
            >
              {limpar.isPending && <Loader2 className="animate-spin" aria-hidden="true" />}
              Apagar definitivamente
            </Botao>
          </DialogoRodape>
        </DialogoConteudo>
      </Dialogo>
    </div>
  );
}
